import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Upload, FileText, Activity, Download, LogOut, Search,
  Loader2, RefreshCw, Eye, Database, Users, CheckCircle, XCircle, Settings, BarChart3, Plug, Shield, Edit3, X, AlertTriangle, Link2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminConnectors from "@/components/AdminConnectors";
import AdminUsers from "@/components/AdminUsers";
import AppUserManagement from "@/components/AppUserManagement";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAdminToken() {
  // June 2026 (Bug 3): elevated LevelUp SSO users (e.g. microsoft_manager) hold
  // only an app_token. Fall back to it so they can reach the Analytics dashboard.
  return localStorage.getItem("admin_token") || localStorage.getItem("app_token");
}

function getAuthHeader() {
  const token = getAdminToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

function authAxios() {
  return axios.create({ headers: getAuthHeader() });
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [resources, setResources] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [userRole, setUserRole] = useState(() => localStorage.getItem("admin_role") || "admin");
  const [userPermissions, setUserPermissions] = useState(() => {
    try {
      const stored = localStorage.getItem("admin_permissions");
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Resource table — server-side pagination + status filtering
  const [resourcePage, setResourcePage] = useState(1);   // 1-indexed (server-side)
  const [resourceTotal, setResourceTotal] = useState(0);
  const [resourceTotalPages, setResourceTotalPages] = useState(1);
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourceStatusFilter, setResourceStatusFilter] = useState("Active,Published");
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState(new Set());
  const [restoringId, setRestoringId] = useState(null);
  const PAGE_SIZE = 50;

  // Phase 3 May 2026 — Library filters
  const [formatFilter, setFormatFilter] = useState("");
  const [roleTagFilter, setRoleTagFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [heroFilter, setHeroFilter] = useState("");
  const ROLE_TAG_OPTIONS = ["Sales/Pre-Sales", "Technical", "GTM"];
  const FORMAT_OPTIONS = ["Course", "Video", "Article", "Whitepaper", "Workshop", "Webinar",
    "eBook", "Certification", "Assessment", "Playbook", "Document",
    "Guide", "Lab", "Module", "Learning Path"];

  // Phase 3 May 2026 — How Plans Are Generated (info panel)
  const [showFlowDoc, setShowFlowDoc] = useState(false);
  const [flowDoc, setFlowDoc] = useState(null);

  // Phase 3 May 2026 — Pending Deletes (super_admin/admin)
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [pendingDeletesLoading, setPendingDeletesLoading] = useState(false);

  // Iter-38b: live Mass AI Re-tag progress (counter ticking down 1,269 → 0)
  const [tagQueue, setTagQueue] = useState(null);
  const tagQueuePollRef = useRef(null);
  const tagQueueNotifiedRef = useRef(false);

  // Iter-39 May 2026: Bulk metadata edit modal
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState("replace");  // replace | add | remove
  const [bulkRoleTags, setBulkRoleTags] = useState([]);
  const [bulkHeroProducts, setBulkHeroProducts] = useState([]);
  const [bulkSolutionArea, setBulkSolutionArea] = useState("");
  const [bulkContentFormat, setBulkContentFormat] = useState("");
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  // Iter-39b: Preview-before-apply
  const [bulkPreview, setBulkPreview] = useState(null);  // { diffs, summary, skipped_out_of_scope }
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);

  // Phase 3 May 2026 — Delete-request flow for RCMs
  const [deleteReqTarget, setDeleteReqTarget] = useState(null);
  const [deleteReqReason, setDeleteReqReason] = useState("");
  const [deleteReqSaving, setDeleteReqSaving] = useState(false);
  const isRcm = (userRole === "resource_content_manager");

  const adminName = localStorage.getItem("admin_name") || "Admin";

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }
    // Load user info
    const storedRole = localStorage.getItem("admin_role");
    const storedPermissions = localStorage.getItem("admin_permissions");
    const perms = storedPermissions ? JSON.parse(storedPermissions) : [];
    setUserRole(storedRole || "admin");
    setUserPermissions(perms);

    // Redirect users with no admin permissions to the Goals dashboard
    if (perms.length === 0) {
      navigate("/my-plans");
      return;
    }
    loadDashboard();
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    const storedPermissions = localStorage.getItem("admin_permissions");
    const perms = storedPermissions ? JSON.parse(storedPermissions) : [];
    
    try {
      const auth = authAxios();
      
      // Always load stats and prompt (analytics permission)
      const [statsRes, promptRes] = await Promise.all([
        auth.get(`${API}/admin/dashboard-stats`),
        axios.get(`${API}/config/system-prompt`),
      ]);
      setStats(statsRes.data);
      setSystemPrompt(promptRes.data.prompt || "");
      
      // Only load sessions if user has permission
      if (perms.includes("sessions")) {
        try {
          const sessionsRes = await auth.get(`${API}/admin/sessions`);
          setSessions(sessionsRes.data);
        } catch (e) {
          console.log("Sessions not accessible");
        }
      }
      
      // Only load audit logs if user has permission
      if (perms.includes("audit_logs")) {
        try {
          const logsRes = await auth.get(`${API}/admin/audit-logs`);
          setAuditLogs(logsRes.data);
        } catch (e) {
          console.log("Audit logs not accessible");
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        navigate("/admin/login");
      } else if (err.response?.status !== 403) {
        toast.error("Failed to load dashboard data");
      }
    }
  }, [navigate]);

  const [retagging, setRetagging] = useState({});
  // Broken-links notification banner (dismissible per check timestamp)
  const [linkBannerDismissed, setLinkBannerDismissed] = useState(() => {
    try { return localStorage.getItem("link_banner_dismissed") || ""; } catch { return ""; }
  });
  // Controlled main tab state so the broken-links banner "View links" can navigate programmatically
  const [activeMainTab, setActiveMainTab] = useState(() => {
    if (userPermissions.includes("analytics")) return "analytics";
    if (userPermissions.includes("connectors")) return "connectors";
    if (userPermissions.includes("resources")) return "resources";
    return "sessions";
  });
  // Inline AI-tags edit dialog state
  const [aiTagsEditTarget, setAiTagsEditTarget] = useState(null);
  const [aiTagsDraft, setAiTagsDraft] = useState("");
  const [heroDraft, setHeroDraft] = useState([]);
  const [solutionAreaDraft, setSolutionAreaDraft] = useState("");
  const [roleTagsDraft, setRoleTagsDraft] = useState([]);  // Phase 3
  // June 2026 (Bug 4b): edit core descriptive fields inline too.
  const [titleDraft, setTitleDraft] = useState("");
  const [urlDraft, setUrlDraft] = useState("");
  const [formatDraft, setFormatDraft] = useState("");
  const [levelDraft, setLevelDraft] = useState("");
  const [aiTagsSaving, setAiTagsSaving] = useState(false);
  const [heroProductOptions, setHeroProductOptions] = useState([]);
  const [solutionAreaOptions, setSolutionAreaOptions] = useState([]);

  // ── Server-side resource loading ───────────────────────────
  const loadResources = useCallback(async (page = 1, statusFilter = "Active,Published", search = "", filters = {}) => {
    setResourcesLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      if (statusFilter) params.set("status", statusFilter);
      if (search && search.length >= 2) params.set("q", search);
      if (filters.format) params.set("format", filters.format);
      if (filters.role_tag) params.set("role_tag", filters.role_tag);
      if (filters.solution_area) params.set("solution_area", filters.solution_area);
      if (filters.hero_product) params.set("hero_product", filters.hero_product);
      const token = getAdminToken();
      const res = await axios.get(`${API}/resources?${params}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      const d = res.data;
      if (d && typeof d === "object" && "data" in d) {
        setResources(d.data || []);
        setResourceTotal(d.total || 0);
        setResourceTotalPages(d.total_pages || 1);
      } else {
        setResources(Array.isArray(d) ? d : []);
        setResourceTotal(Array.isArray(d) ? d.length : 0);
        setResourceTotalPages(1);
      }
    } catch {
      toast.error("Failed to load resources");
    } finally {
      setResourcesLoading(false);
    }
  }, []);

  // Reload resources when status filter, page, search, or library filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      loadResources(resourcePage, resourceStatusFilter, resourceSearch, {
        format: formatFilter,
        role_tag: roleTagFilter,
        solution_area: areaFilter,
        hero_product: heroFilter,
      });
    }, resourceSearch.length > 0 ? 400 : 0);
    return () => clearTimeout(debounce);
  }, [resourcePage, resourceStatusFilter, resourceSearch, formatFilter, roleTagFilter, areaFilter, heroFilter, loadResources]);

  // Load dropdown options once for the inline editor
  useEffect(() => {
    axios.get(`${API}/config/dropdowns`)
      .then(res => {
        setHeroProductOptions(res.data?.hero_products || []);
        setSolutionAreaOptions(res.data?.solution_areas || []);
      })
      .catch(() => { /* non-blocking */ });
  }, []);

  const handleRestoreResource = async (resourceId) => {
    setRestoringId(resourceId);
    try {
      const token = getAdminToken();
      await axios.post(`${API}/admin/resources/${resourceId}/restore`, {}, {
        headers: { authorization: `Bearer ${token}` },
      });
      toast.success(`Resource ${resourceId} restored to Active`);
      loadResources(resourcePage, resourceStatusFilter, resourceSearch);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  const handleRetag = async (resourceId) => {
    setRetagging(prev => ({ ...prev, [resourceId]: true }));
    try {
      const token = getAdminToken();
      const res = await axios.post(`${API}/admin/resources/${resourceId}/tag`, {}, {
        headers: { authorization: `Bearer ${token}` },
      });
      toast.success(`AI tags generated for ${resourceId}`);
      // Update the resource in state
      setResources(prev => prev.map(r =>
        r.resource_id === resourceId
          ? { ...r, ai_tagged: true, ai_tags: res.data.ai_tags }
          : r
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI tagging failed");
    } finally {
      setRetagging(prev => ({ ...prev, [resourceId]: false }));
    }
  };

  const openAiTagsEditor = (resource) => {
    setAiTagsEditTarget(resource);
    setAiTagsDraft(resource.ai_tags?.flat_tags || "");
    setHeroDraft(resource.hero_products || []);
    setSolutionAreaDraft(resource.solution_area || "");
    setRoleTagsDraft(resource.role_tags || []);
    setTitleDraft(resource.title || "");
    setUrlDraft(resource.url || "");
    setFormatDraft(resource.content_format || resource.resource_format || "");
    setLevelDraft(resource.skill_level || "");
  };

  const handleSaveAiTags = async () => {
    if (!aiTagsEditTarget) return;
    setAiTagsSaving(true);
    try {
      const token = getAdminToken();
      const rid = aiTagsEditTarget.resource_id;
      const payload = {
        ai_tags_flat: aiTagsDraft,
        hero_products: heroDraft,
        solution_area: solutionAreaDraft,
        role_tags: roleTagsDraft,
        title: titleDraft,
        url: urlDraft,
        content_format: formatDraft,
        skill_level: levelDraft,
      };
      const res = await axios.put(
        `${API}/admin/resources/${rid}/ai-tags`,
        payload,
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(`Resource ${rid} updated — AI re-tag queued in background`);
      setResources(prev => prev.map(r =>
        r.resource_id === rid
          ? {
              ...r,
              ai_tagged: res.data.ai_tagged ?? r.ai_tagged,
              ai_tags: res.data.ai_tags ?? r.ai_tags,
              hero_products: res.data.hero_products ?? r.hero_products,
              solution_area: res.data.solution_area ?? r.solution_area,
              role_tags: res.data.role_tags ?? r.role_tags,
              title: res.data.title ?? r.title,
              url: res.data.url ?? r.url,
              content_format: res.data.content_format ?? r.content_format,
              resource_format: res.data.content_format ?? r.resource_format,
              skill_level: res.data.skill_level ?? r.skill_level,
              ai_confidence_score: res.data.ai_confidence_score ?? r.ai_confidence_score,
              product_coverage: res.data.product_coverage ?? r.product_coverage,
            }
          : r
      ));
      setAiTagsEditTarget(null);
      setAiTagsDraft("");
      setHeroDraft([]);
      setSolutionAreaDraft("");
      setRoleTagsDraft([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update resource");
    } finally {
      setAiTagsSaving(false);
    }
  };
  // ── Phase 3 May 2026: Mass AI re-tag (super_admin only) ────
  const fireCompletionNotice = useCallback((snap) => {
    // Persistent toast (stays until manually dismissed)
    toast.success(
      `Mass AI Re-tag complete — ${snap.run_total.toLocaleString()} resources updated`,
      {
        duration: Infinity,
        description: "Hero products and role tags have been refreshed in the background.",
        action: { label: "Dismiss", onClick: () => {} },
      }
    );
    // Desktop notification (if user granted permission)
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const n = new Notification("MyGoals — AI Re-tagging Complete", {
          body: `${snap.run_total.toLocaleString()} resources have been re-tagged.`,
          icon: "/favicon.ico",
          tag: "mygoals-mass-retag-done",
          requireInteraction: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
      }
    } catch { /* notifications not supported */ }
  }, []);

  const fetchTagQueueStatus = useCallback(async () => {
    try {
      const token = getAdminToken();
      const res = await axios.get(`${API}/admin/tagging-queue-status`, {
        headers: { authorization: `Bearer ${token}` },
      });
      const snap = res.data;
      setTagQueue(prev => {
        // Detect active→inactive transition (a run just finished) and fire
        // the completion notification exactly once.
        if (prev && prev.active && snap && !snap.active && snap.run_total > 0 && !tagQueueNotifiedRef.current) {
          tagQueueNotifiedRef.current = true;
          fireCompletionNotice(snap);
        }
        return snap;
      });
      // Once the queue drains AND we had an active run, stop polling.
      if (snap && !snap.active && tagQueuePollRef.current) {
        clearInterval(tagQueuePollRef.current);
        tagQueuePollRef.current = null;
      }
    } catch { /* poller silently ignores transient failures */ }
  }, [fireCompletionNotice]);

  const startTagQueuePolling = useCallback(() => {
    if (tagQueuePollRef.current) clearInterval(tagQueuePollRef.current);
    // tick every 2.5s while a run is active
    tagQueuePollRef.current = setInterval(fetchTagQueueStatus, 2500);
    fetchTagQueueStatus();
  }, [fetchTagQueueStatus]);

  // Stop polling on unmount
  useEffect(() => () => {
    if (tagQueuePollRef.current) clearInterval(tagQueuePollRef.current);
  }, []);

  // Kick off polling once on mount in case a run is already in flight
  useEffect(() => {
    if (isRcm) return;
    fetchTagQueueStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRcm]);

  useEffect(() => {
    if (tagQueue && tagQueue.active && !tagQueuePollRef.current) {
      startTagQueuePolling();
    }
  }, [tagQueue, startTagQueuePolling]);

  const handleMassRetag = async (onlyMissingTags = false) => {
    const fastMode = !!onlyMissingTags;
    const untaggedCount = tagQueue?.untagged_count ?? null;
    const confirmMsg = fastMode
      ? (`Re-tag only NEW / untagged resources?\n\n` +
         `${untaggedCount !== null ? `~${untaggedCount} resource(s) need tagging.\n\n` : ""}` +
         `Typically finishes in under a minute. Use this after a CSV ingest or routine top-up.`)
      : (`Re-tag ALL Active / Published resources?\n\n` +
         `Full run — depending on volume can take 10–30 minutes. ` +
         `Use this after updating the keyword bank or AI prompt.\n\n` +
         `We'll show a live progress indicator and send you a desktop notification when it finishes.`);
    if (!window.confirm(confirmMsg)) return;
    // Ask for desktop-notification permission BEFORE kicking off the job
    try {
      if ("Notification" in window && Notification.permission === "default") {
        await Notification.requestPermission();
      }
    } catch { /* ignore — fallback to persistent toast only */ }
    try {
      const token = getAdminToken();
      const url = `${API}/admin/resources/mass-retag${fastMode ? "?only_missing_tags=true" : ""}`;
      const res = await axios.post(
        url,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      if (res.data.queued === 0) {
        toast.success("Nothing to re-tag — every resource already has AI tags.", { duration: 5000 });
        return;
      }
      toast.success(
        `Queued ${res.data.queued} ${fastMode ? "untagged " : ""}resource(s) for AI re-tagging. Watch the live progress indicator above.`,
        { duration: 6000 }
      );
      tagQueueNotifiedRef.current = false;
      if (res.data.queue_status) setTagQueue(res.data.queue_status);
      startTagQueuePolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mass re-tag failed");
    }
  };

  const [assigningHero, setAssigningHero] = useState(false);
  const handleMassAssignHeroProducts = async () => {
    if (!window.confirm(
      `Auto-assign Hero Products to External resources that are missing one?\n\n` +
      `This analyses each resource's name, URL and AI tags and assigns the strongest-matching Hero Product automatically. ` +
      `Runs in a few seconds. You can refine any assignment afterwards via Bulk Edit.`
    )) return;
    setAssigningHero(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/resources/mass-assign-hero-products?only_blank=true`,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Assigned ${res.data.assigned} hero products.`, { duration: 8000 });
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mass hero-product assignment failed");
    } finally {
      setAssigningHero(false);
    }
  };

  const [syncingSales, setSyncingSales] = useState(false);
  const handleSyncLevelUpSales = async () => {
    if (!window.confirm(
      `Sync Sales / Pre-Sales courses from LevelUp into the Resource Library?\n\n` +
      `Imports every LevelUp course whose role is Sales/Pre-Sales OR whose Course Type is "Sales Ready" / "Tech Deal Ready", ` +
      `tags them "Sales/Pre-Sales" and publishes them. Duplicates are skipped automatically.`
    )) return;
    setSyncingSales(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/resources/sync-levelup-sales`,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Synced ${res.data.qualifying} courses.`, { duration: 8000 });
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "LevelUp Sales sync failed");
    } finally {
      setSyncingSales(false);
    }
  };

  // ── Phase 3 May 2026: How Plans Are Generated panel ──────
  const loadFlowDoc = async () => {
    setShowFlowDoc(true);
    if (flowDoc) return;
    try {
      const token = getAdminToken();
      const res = await axios.get(`${API}/admin/plan-flow-examples`, {
        headers: { authorization: `Bearer ${token}` },
      });
      setFlowDoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load plan-flow examples");
    }
  };

  // ── Phase 3 May 2026: Pending-deletes (super_admin / admin) ─
  const loadPendingDeletes = useCallback(async () => {
    if (isRcm) return;
    setPendingDeletesLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.get(`${API}/admin/resources/pending-deletes`, {
        headers: { authorization: `Bearer ${token}` },
      });
      setPendingDeletes(Array.isArray(res.data) ? res.data : []);
    } catch {
      // silently ignore — RCMs and viewers don't have access
    } finally {
      setPendingDeletesLoading(false);
    }
  }, [isRcm]);

  useEffect(() => { loadPendingDeletes(); }, [loadPendingDeletes]);

  const handleDeleteDecision = async (resourceId, action) => {
    const note = action === "reject" ? (window.prompt("Optional rejection note for the requester:") || "") : "";
    if (action === "approve" && !window.confirm(`Archive this resource (soft-delete)? It will be moved to Archived status.`)) return;
    try {
      const token = getAdminToken();
      await axios.post(
        `${API}/admin/resources/${resourceId}/delete-decision`,
        { action, note },
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(action === "approve" ? "Resource archived (soft-deleted)" : "Delete request rejected");
      setPendingDeletes(prev => prev.filter(p => p.resource_id !== resourceId));
      loadResources(resourcePage, resourceStatusFilter, resourceSearch, {
        format: formatFilter, role_tag: roleTagFilter, solution_area: areaFilter, hero_product: heroFilter,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Decision failed");
    }
  };

  // ── Phase 3 May 2026: RCM delete-request flow ────────────
  const openDeleteRequest = (resource) => {
    setDeleteReqTarget(resource);
    setDeleteReqReason("");
  };

  const submitDeleteRequest = async () => {
    if (!deleteReqTarget) return;
    setDeleteReqSaving(true);
    try {
      const token = getAdminToken();
      await axios.post(
        `${API}/admin/resources/${deleteReqTarget.resource_id}/request-delete`,
        { reason: deleteReqReason },
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success("Delete request submitted for super-admin review");
      setResources(prev => prev.map(r =>
        r.resource_id === deleteReqTarget.resource_id
          ? { ...r, delete_request_status: "pending", delete_request_reason: deleteReqReason }
          : r
      ));
      setDeleteReqTarget(null);
      setDeleteReqReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete request failed");
    } finally {
      setDeleteReqSaving(false);
    }
  };

  // Iter-39 May 2026: Bulk-edit — payload builder shared between Preview + Apply
  const buildBulkPayload = () => {
    const ids = Array.from(selectedResourceIds);
    const payload = { resource_ids: ids, mode: bulkEditMode };
    let anyField = false;
    if (bulkRoleTags.length > 0) { payload.role_tags = bulkRoleTags; anyField = true; }
    if (bulkHeroProducts.length > 0) { payload.hero_products = bulkHeroProducts; anyField = true; }
    if (bulkSolutionArea && bulkEditMode === "replace") { payload.solution_area = bulkSolutionArea; anyField = true; }
    if (bulkContentFormat && bulkEditMode === "replace") { payload.content_format = bulkContentFormat; anyField = true; }
    return { payload, anyField, ids };
  };

  // Iter-39b: Step 1 — fetch preview (no DB write)
  const previewBulkEdit = async () => {
    const { payload, anyField, ids } = buildBulkPayload();
    if (ids.length === 0) return;
    if (!anyField) {
      toast.error("Pick at least one field to update");
      return;
    }
    setBulkPreviewLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/resources/bulk-edit-preview`,
        payload,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setBulkPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  // Iter-39b: Step 2 — apply the previously-previewed payload
  const confirmBulkEdit = async () => {
    const { payload, anyField } = buildBulkPayload();
    if (!anyField) {
      toast.error("Pick at least one field to update");
      return;
    }
    setBulkEditSaving(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/resources/bulk-edit`,
        payload,
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Updated ${res.data.updated} resource(s)`);
      setBulkEditOpen(false);
      setBulkPreview(null);
      setSelectedResourceIds(new Set());
      loadResources(resourcePage, resourceStatusFilter, resourceSearch, {
        format: formatFilter, role_tag: roleTagFilter, solution_area: areaFilter, hero_product: heroFilter,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk edit failed");
    } finally {
      setBulkEditSaving(false);
    }
  };

  // Helper — render an array as either a chip-list or an em-dash placeholder
  const _formatArr = (arr) => (Array.isArray(arr) && arr.length > 0) ? arr.join(", ") : "—";

  const handleResourceIngest = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(prev => ({ ...prev, ingest: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = getAdminToken();
      const res = await axios.post(`${API}/admin/resources/ingest`, formData, {
        headers: { "Content-Type": "multipart/form-data", authorization: `Bearer ${token}` },
      });
      toast.success(`Ingested: ${res.data.staged} staged, ${res.data.skipped} skipped — review in Connectors → Staging`);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ingest failed");
    } finally {
      setLoading(prev => ({ ...prev, ingest: false }));
      e.target.value = "";
    }
  };

  const handleResourceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(prev => ({ ...prev, upload: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = getAdminToken();
      await axios.post(`${API}/admin/resources/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data", authorization: `Bearer ${token}` },
      });
      toast.success("Resources uploaded successfully");
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(prev => ({ ...prev, upload: false }));
      e.target.value = "";
    }
  };

  const handleDownloadTemplate = async (format = "csv") => {
    try {
      const token = getAdminToken();
      const res = await axios.get(`${API}/admin/resources/template?format=${format}`, {
        headers: { authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const mime = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mygoals_resource_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Template downloaded${format === "xlsx" ? " (Excel with dropdowns)" : ""} — fill it in and use 'Ingest CSV / Excel'`);
    } catch {
      toast.error("Template download failed");
    }
  };

  const handleBulkRetag = async () => {
    if (selectedResourceIds.size === 0) return;
    const ids = Array.from(selectedResourceIds);
    if (!window.confirm(`Re-tag ${ids.length} resource(s) with AI? Tagging runs in the background — refresh in a few seconds to see updated tags.`)) return;
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/resources/bulk-retag`,
        { resource_ids: ids },
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(`Queued ${res.data.queued} resource(s) for AI re-tagging${res.data.skipped ? ` (${res.data.skipped} skipped)` : ""}`);
      setSelectedResourceIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk re-tag failed");
    }
  };

  const handleBulkDeleteResources = async () => {
    if (selectedResourceIds.size === 0) return;
    const ids = Array.from(selectedResourceIds);
    if (!window.confirm(`Delete ${ids.length} resource(s)? This cannot be undone.`)) return;
    try {
      const token = getAdminToken();
      const res = await axios.delete(`${API}/admin/resources/bulk`, {
        headers: { authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        data: { resource_ids: ids },
      });
      toast.success(`Deleted ${res.data.deleted} resource(s)`);
      setSelectedResourceIds(new Set());
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleMoveToStaging = async () => {
    if (selectedResourceIds.size === 0) return;
    const ids = Array.from(selectedResourceIds);
    if (!window.confirm(`Un-publish ${ids.length} resource(s) and move them back to Staging for editing?`)) return;
    try {
      const token = getAdminToken();
      const res = await axios.post(
        `${API}/admin/staging/bulk`,
        { resource_ids: ids, action: "move_to_staging" },
        { headers: { authorization: `Bearer ${token}` } }
      );
      toast.success(`Moved ${res.data.modified} resource(s) back to Staging — edit them in Connectors → Staging`);
      setSelectedResourceIds(new Set());
      // Refresh current page
      loadResources(resourcePage, resourceStatusFilter, resourceSearch);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move to Staging failed");
    }
  };

  const handleDownloadAnalytics = async () => {
    try {
      const token = getAdminToken();
      const res = await axios.get(`${API}/admin/analytics/download`, {
        headers: { authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mygoals_analytics.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Analytics downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const handleViewSession = async (sessionId) => {
    try {
      const auth = authAxios();
      const res = await auth.get(`${API}/admin/sessions/${sessionId}`);
      setSelectedSession(res.data);
    } catch {
      toast.error("Failed to load session details");
    }
  };

  const handleUpdatePrompt = async () => {
    setPromptLoading(true);
    try {
      const token = getAdminToken();
      await axios.put(`${API}/admin/config/system-prompt`, { prompt: systemPrompt }, {
        headers: { authorization: `Bearer ${token}` },
      });
      toast.success("System prompt updated");
    } catch {
      toast.error("Failed to update system prompt");
    } finally {
      setPromptLoading(false);
    }
  };

  const handleDropdownUpload = async (configName, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = getAdminToken();
      await axios.post(`${API}/admin/config/dropdowns/${configName}`, formData, {
        headers: { "Content-Type": "multipart/form-data", authorization: `Bearer ${token}` },
      });
      toast.success(`${configName} updated successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const { logout: authLogout } = useAuth();

  const handleLogout = () => {
    // Dismiss any pending toasts before clearing auth
    toast.dismiss();
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_permissions");
    localStorage.removeItem("app_token");
    authLogout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FFF8F3]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#194449] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-6 brightness-0 invert" />
            <div className="w-px h-6 bg-white/30" />
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MyGoals <span className="text-white/60 font-normal text-sm ml-1">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <img src="/ms-sponsored-logo.svg" alt="Sponsored by Microsoft" className="h-8 hidden md:block" />
            <button
              onClick={() => navigate("/intake")}
              className="text-sm text-white/70 hover:text-white transition-colors hidden md:flex items-center gap-1.5"
              data-testid="admin-my-learning-btn"
            >
              <Sparkles className="w-4 h-4" />
              My Learning
            </button>
            <span className="text-sm text-white/70">{adminName}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total Sessions", value: stats.total_sessions, icon: Users, color: "#194449" },
              { label: "Learning Plans", value: stats.successful_plans, icon: CheckCircle, color: "#07641D" },
              { label: "Failed Plans", value: stats.failed_plans, icon: XCircle, color: "#DC2626" },
              { label: "Active Resources", value: stats.active_resources, icon: Database, color: "#194449" },
              { label: "Total Events", value: stats.total_events || 0, icon: BarChart3, color: "#07641D" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl border border-black/5 p-5 shadow-sm" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}10` }}>
                    <stat.icon className="w-5 h-5" style={{ color: stat.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-[#091F2C]">{stat.value}</div>
                    <div className="text-xs text-[#64748B]">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Broken Links Notification Banner */}
        {stats && stats.broken_links_count > 0 && stats.link_check_status === "done" &&
          linkBannerDismissed !== stats.link_check_at && (
          <div
            className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6"
            data-testid="broken-links-banner"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#091F2C]">
                {stats.broken_links_count} broken {stats.broken_links_count === 1 ? "link" : "links"} detected
              </p>
              <p className="text-xs text-[#64748B] mt-0.5">
                Last link health check on {stats.link_check_at ? new Date(stats.link_check_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC", timeZoneName: "short" }) : "—"} found resources with unreachable URLs.
              </p>
            </div>
            <button
              onClick={() => {
                // Switch to Connectors tab via React state (reliable, no DOM hacks)
                setActiveMainTab("connectors");
                // Then click into Link Health sub-tab (slight delay for tab content to mount)
                setTimeout(() => {
                  const healthTab = document.querySelector('[data-testid="connectors-tab-health"]');
                  if (healthTab) healthTab.click();
                }, 200);
              }}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap mr-3"
              data-testid="broken-links-view-btn"
            >
              <Link2 className="w-3.5 h-3.5" />
              View links
            </button>
            <button
              onClick={() => {
                setLinkBannerDismissed(stats.link_check_at);
                try { localStorage.setItem("link_banner_dismissed", stats.link_check_at); } catch {}
              }}
              className="text-amber-400 hover:text-amber-600 transition-colors flex-shrink-0 mt-0.5"
              aria-label="Dismiss broken links notification"
              data-testid="broken-links-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Top-level Tabs: Analytics + Management */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
          <TabsList className="bg-white border border-black/5 p-1 rounded-lg mb-6 flex-wrap">
            {userPermissions.includes("analytics") && (
              <TabsTrigger value="analytics" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-analytics">
                <BarChart3 className="w-4 h-4 mr-2" /> Analytics
              </TabsTrigger>
            )}
            {userPermissions.includes("connectors") && (
              <TabsTrigger value="connectors" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-connectors">
                <Plug className="w-4 h-4 mr-2" /> Connectors
              </TabsTrigger>
            )}
            {userPermissions.includes("resources") && (
              <TabsTrigger value="resources" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-resources">
                <Database className="w-4 h-4 mr-2" /> Resources
              </TabsTrigger>
            )}
            {userPermissions.includes("sessions") && (
              <TabsTrigger value="sessions" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-sessions">
                <Activity className="w-4 h-4 mr-2" /> Sessions
              </TabsTrigger>
            )}
            {userPermissions.includes("audit_logs") && (
              <TabsTrigger value="audit" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-audit">
                <FileText className="w-4 h-4 mr-2" /> Audit Logs
              </TabsTrigger>
            )}
            {userPermissions.includes("config") && (
              <TabsTrigger value="config" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-config">
                <Settings className="w-4 h-4 mr-2" /> Config
              </TabsTrigger>
            )}
            {userPermissions.includes("users") && (
              <TabsTrigger value="users" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-users">
                <Shield className="w-4 h-4 mr-2" /> Users
              </TabsTrigger>
            )}
            {userPermissions.includes("users") && (
              <TabsTrigger value="app-users" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md" data-testid="tab-app-users">
                <Users className="w-4 h-4 mr-2" /> LevelUp Users
              </TabsTrigger>
            )}
          </TabsList>

          {/* Analytics Tab - Full Dashboard */}
          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>

          {/* Connectors Tab - Indexing & Staging */}
          <TabsContent value="connectors">
            <AdminConnectors />
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            {/* Iter-38b — Live Mass AI Re-tag progress indicator */}
            {!isRcm && tagQueue && (tagQueue.active || tagQueue.run_total > 0) && (
              <div className="bg-gradient-to-br from-[#194449] to-[#0F2D31] text-white rounded-xl shadow-md mb-4 overflow-hidden" data-testid="tag-queue-progress-card">
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        {tagQueue.active ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Sparkles className="w-5 h-5 text-[#C7F7C2]" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {tagQueue.active ? "AI Re-tagging in Progress" : "AI Re-tagging Complete"}
                        </div>
                        <div className="text-[11px] text-white/70 mt-0.5">
                          {tagQueue.active ? (
                            <>
                              <span data-testid="tag-queue-completed">{tagQueue.completed_in_current_run.toLocaleString()}</span>
                              {" / "}
                              <span data-testid="tag-queue-total">{tagQueue.run_total.toLocaleString()}</span>
                              {" resources processed — "}
                              <span data-testid="tag-queue-in-progress">{tagQueue.in_progress}</span>
                              {" running concurrently · "}
                              <span data-testid="tag-queue-remaining">{tagQueue.queued.toLocaleString()}</span>
                              {" in queue"}
                            </>
                          ) : (
                            <>All {tagQueue.run_total.toLocaleString()} resources have been re-tagged.</>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-3xl font-bold tabular-nums" data-testid="tag-queue-percent">
                        {tagQueue.percent_complete.toFixed(1)}%
                      </div>
                      {tagQueue.active && (
                        <div className="text-[10px] text-white/60 uppercase tracking-wider">live · 2.5s</div>
                      )}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${tagQueue.active ? "bg-[#C7F7C2]" : "bg-[#07641D]"}`}
                      style={{ width: `${Math.max(0.5, tagQueue.percent_complete)}%` }}
                      data-testid="tag-queue-progress-bar"
                    />
                  </div>
                  {!tagQueue.active && tagQueue.run_total > 0 && (
                    <button
                      onClick={() => setTagQueue(null)}
                      className="mt-2 text-[10px] text-white/70 hover:text-white underline"
                      data-testid="tag-queue-dismiss"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Phase 3 May 2026 — Pending Deletes panel (super-admin / admin) */}
            {!isRcm && pendingDeletes.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl shadow-sm mb-4" data-testid="pending-deletes-panel">
                <div className="p-4 border-b border-orange-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-orange-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Pending Delete Requests ({pendingDeletes.length})
                    </h3>
                    <p className="text-xs text-orange-700 mt-0.5">
                      Resource Content Managers have flagged these for removal. Review and approve / reject.
                    </p>
                  </div>
                  <button onClick={loadPendingDeletes} className="text-xs text-orange-700 hover:underline" data-testid="refresh-pending-deletes">
                    {pendingDeletesLoading ? "…" : "Refresh"}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Title</TableHead>
                        <TableHead className="text-xs">Requested By</TableHead>
                        <TableHead className="text-xs">Reason</TableHead>
                        <TableHead className="text-xs">Requested At</TableHead>
                        <TableHead className="text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingDeletes.map(pd => (
                        <TableRow key={pd.resource_id} data-testid={`pending-delete-row-${pd.resource_id}`}>
                          <TableCell className="text-xs max-w-[260px]">
                            <div className="font-medium text-[#091F2C] truncate" title={pd.title}>{pd.title}</div>
                            <div className="text-[10px] font-mono text-[#64748B] mt-0.5">{pd.resource_id}</div>
                          </TableCell>
                          <TableCell className="text-xs">{pd.delete_requested_by || "—"}</TableCell>
                          <TableCell className="text-xs max-w-[300px] truncate" title={pd.delete_request_reason}>{pd.delete_request_reason || <span className="italic text-[#94A3B8]">no reason given</span>}</TableCell>
                          <TableCell className="text-xs">{pd.delete_requested_at ? new Date(pd.delete_requested_at).toLocaleString() : "—"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDeleteDecision(pd.resource_id, "approve")}
                                className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                data-testid={`approve-delete-${pd.resource_id}`}
                              >Approve & Delete</button>
                              <button
                                onClick={() => handleDeleteDecision(pd.resource_id, "reject")}
                                className="text-[10px] px-2 py-1 rounded border border-black/10 text-[#64748B] hover:bg-white"
                                data-testid={`reject-delete-${pd.resource_id}`}
                              >Reject</button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-black/5 shadow-sm">
              <div className="p-5 border-b border-black/5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Resource Library ({resources.length})
                  </h3>
                  {selectedResourceIds.size > 0 && (
                    <>
                      {!isRcm && (resourceStatusFilter === "Active,Published" || resourceStatusFilter === "Active" || resourceStatusFilter === "Published") && (
                        <button
                          onClick={handleMoveToStaging}
                          className="inline-flex items-center gap-1.5 bg-yellow-500 text-white hover:bg-yellow-600 rounded-full px-4 py-1.5 text-xs font-medium transition-all"
                          data-testid="bulk-move-to-staging-btn"
                          title="Un-publish and move back to Staging for editing AI tags, hero products, solution area"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Move to Staging ({selectedResourceIds.size})
                        </button>
                      )}
                      <button
                        onClick={handleBulkRetag}
                        className="inline-flex items-center gap-1.5 bg-[#194449] text-white hover:bg-[#194449]/85 rounded-full px-4 py-1.5 text-xs font-medium transition-all"
                        data-testid="bulk-retag-resources-btn"
                        title="Queue AI re-tagging for the selected resources"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Re-tag with AI ({selectedResourceIds.size})
                      </button>
                      <button
                        onClick={() => {
                          setBulkEditMode("replace");
                          setBulkRoleTags([]);
                          setBulkHeroProducts([]);
                          setBulkSolutionArea("");
                          setBulkContentFormat("");
                          setBulkPreview(null);
                          setBulkEditOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 bg-[#07641D] text-white hover:bg-[#07641D]/90 rounded-full px-4 py-1.5 text-xs font-medium transition-all"
                        data-testid="bulk-edit-resources-btn"
                        title="Bulk-edit Role / Hero Products / Solution Area / Format on selected resources"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Bulk Edit ({selectedResourceIds.size})
                      </button>
                      {!isRcm && (
                        <button
                          onClick={handleBulkDeleteResources}
                          className="inline-flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 rounded-full px-4 py-1.5 text-xs font-medium transition-all"
                          data-testid="bulk-delete-resources-btn"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete Selected ({selectedResourceIds.size})
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="flex gap-3 flex-wrap">
                  {!isRcm && (
                    <>
                      <button
                        onClick={() => handleMassRetag(true)}
                        className={`relative inline-flex items-center gap-2 bg-[#07641D] text-white hover:bg-[#07641D]/90 rounded-full px-5 py-2 text-sm font-medium transition-all ${
                          (tagQueue?.untagged_count ?? 0) > 0 && !tagQueue?.active
                            ? "ring-2 ring-[#07641D]/40 ring-offset-2 ring-offset-[#FFFCFA] shadow-[0_0_0_4px_rgba(7,100,29,0.15)] animate-[retagPulse_2s_ease-in-out_infinite]"
                            : ""
                        }`}
                        data-testid="retag-untagged-btn"
                        title={(tagQueue?.untagged_count ?? 0) > 0
                          ? `${tagQueue.untagged_count.toLocaleString()} resource(s) need tagging — click to re-tag only those`
                          : "Fast: re-tag only resources missing AI tags or hero products"}
                      >
                        <Sparkles className={`w-4 h-4 ${(tagQueue?.untagged_count ?? 0) > 0 && !tagQueue?.active ? "animate-pulse" : ""}`} />
                        Re-tag New / Untagged Only
                        {tagQueue?.untagged_count != null && tagQueue.untagged_count > 0 && (
                          <span className="ml-1 bg-white text-[#07641D] text-[10px] px-1.5 py-0.5 rounded-full font-semibold" data-testid="untagged-count-badge">
                            {tagQueue.untagged_count.toLocaleString()}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleMassRetag(false)}
                        className="inline-flex items-center gap-2 border border-[#194449] text-[#194449] hover:bg-[#194449]/5 rounded-full px-5 py-2 text-sm font-medium transition-all"
                        data-testid="mass-retag-btn"
                        title="Full run: re-tag ALL Active resources (10-30 min). Use after keyword bank or AI prompt updates."
                      >
                        <RefreshCw className="w-4 h-4" /> Re-tag Everything
                      </button>
                      <button
                        onClick={handleMassAssignHeroProducts}
                        disabled={assigningHero}
                        className="inline-flex items-center gap-2 border border-[#07641D] text-[#07641D] hover:bg-[#07641D]/5 rounded-full px-5 py-2 text-sm font-medium transition-all disabled:opacity-50"
                        data-testid="mass-assign-hero-btn"
                        title="Auto-assign the most relevant Hero Product to External resources that are missing one (analyses name, URL + AI tags)."
                      >
                        <Sparkles className="w-4 h-4" /> {assigningHero ? "Assigning…" : "Auto-assign Hero Products"}
                      </button>
                      <button
                        onClick={handleSyncLevelUpSales}
                        disabled={syncingSales}
                        className="inline-flex items-center gap-2 border border-[#194449] text-[#194449] hover:bg-[#194449]/5 rounded-full px-5 py-2 text-sm font-medium transition-all disabled:opacity-50"
                        data-testid="sync-levelup-sales-btn"
                        title="Import Sales / Pre-Sales courses from LevelUp (role = Sales/Pre-Sales or Course Type = Sales Ready / Tech Deal Ready) and publish them."
                      >
                        <Download className="w-4 h-4" /> {syncingSales ? "Syncing…" : "Sync LevelUp Sales/Pre-Sales"}
                      </button>
                    </>
                  )}
                  <button
                    onClick={loadFlowDoc}
                    className="inline-flex items-center gap-2 border border-black/10 text-[#64748B] hover:bg-[#F4F3F5] rounded-full px-5 py-2 text-sm font-medium transition-all"
                    data-testid="show-flow-doc-btn"
                    title="View how the recommendation engine selects + sequences resources"
                  >
                    <FileText className="w-4 h-4" /> How Plans Are Generated
                  </button>
                  <button
                    onClick={() => handleDownloadTemplate("xlsx")}
                    className="inline-flex items-center gap-2 border border-[#07641D] text-[#07641D] hover:bg-[#07641D]/5 rounded-full px-5 py-2 text-sm font-medium transition-all"
                    data-testid="download-template-xlsx-btn"
                    title="Excel template with cascading dropdowns + Role Tags column"
                  >
                    <Download className="w-4 h-4" /> Download Template (Excel · with smart dropdowns)
                  </button>
                  <label className="cursor-pointer" data-testid="ingest-resources-btn" title="Stage CSV/Excel through review workflow">
                    <div className="inline-flex items-center gap-2 bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-5 py-2 text-sm font-medium transition-all">
                      {loading.ingest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Ingest CSV / Excel
                    </div>
                    <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleResourceIngest} />
                  </label>
                  {!isRcm && (
                    <label className="cursor-pointer" data-testid="upload-resources-btn" title="Replace all resources (destructive)">
                      <div className="inline-flex items-center gap-2 border border-black/10 text-[#64748B] hover:bg-[#F4F3F5] rounded-full px-5 py-2 text-sm font-medium transition-all">
                        {loading.upload ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Replace All (CSV)
                      </div>
                      <input type="file" accept=".csv" className="hidden" onChange={handleResourceUpload} />
                    </label>
                  )}
                </div>
              </div>

              {/* Status filter tabs + search + pagination */}
              {(() => {
                const isArchived = resourceStatusFilter === "Archived";
                return (
                  <>
                    {/* Status tabs */}
                    <div className="px-5 pt-4 pb-0 border-b border-black/5">
                      <div className="flex items-center gap-1 overflow-x-auto pb-0">
                        {[
                          { label: "Active / Published", value: "Active,Published" },
                          { label: "Staging", value: "Staging" },
                          { label: "Archived", value: "Archived" },
                          { label: "All", value: "" },
                        ].map(({ label, value }) => (
                          <button
                            key={value}
                            onClick={() => { setResourceStatusFilter(value); setResourcePage(1); }}
                            className={`px-4 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                              resourceStatusFilter === value
                                ? "border-[#194449] text-[#194449] font-semibold"
                                : "border-transparent text-[#64748B] hover:text-[#091F2C] hover:border-black/20"
                            }`}
                            data-testid={`resource-status-tab-${value || "all"}`}
                          >
                            {label}
                          </button>
                        ))}
                        <span className="ml-auto text-xs text-[#64748B] whitespace-nowrap pb-2 pr-1">
                          {resourcesLoading ? "Loading…" : `${resourceTotal.toLocaleString()} resources`}
                        </span>
                      </div>
                    </div>

                    {/* Search bar */}
                    <div className="px-5 py-3 border-b border-black/5 flex flex-wrap items-center gap-3">
                      <input
                        type="text"
                        placeholder="Search by title, ID, product, area…"
                        value={resourceSearch}
                        onChange={e => { setResourceSearch(e.target.value); setResourcePage(1); }}
                        className="border border-black/10 rounded-full px-4 py-1.5 text-sm flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#194449]/20"
                        data-testid="resource-search-input"
                      />
                      {resourcesLoading && <Loader2 className="w-4 h-4 animate-spin text-[#64748B]" />}
                    </div>

                    {/* Phase 3 May 2026 — Library filters (Format, Role, Solution Area, Hero Product) */}
                    <div className="px-5 py-2.5 border-b border-black/5 flex flex-wrap items-center gap-2 bg-[#FFFCFA]">
                      <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Filter:</span>
                      <Select value={formatFilter || "__all__"} onValueChange={v => { setFormatFilter(v === "__all__" ? "" : v); setResourcePage(1); }}>
                        <SelectTrigger className="h-7 text-xs w-[140px] border-black/10" data-testid="filter-format">
                          <SelectValue placeholder="Format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Formats</SelectItem>
                          {FORMAT_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={roleTagFilter || "__all__"} onValueChange={v => { setRoleTagFilter(v === "__all__" ? "" : v); setResourcePage(1); }}>
                        <SelectTrigger className="h-7 text-xs w-[140px] border-black/10" data-testid="filter-role-tag">
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Roles</SelectItem>
                          {ROLE_TAG_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={areaFilter || "__all__"} onValueChange={v => { setAreaFilter(v === "__all__" ? "" : v); setResourcePage(1); }}>
                        <SelectTrigger className="h-7 text-xs w-[170px] border-black/10" data-testid="filter-area">
                          <SelectValue placeholder="Solution Area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Solution Areas</SelectItem>
                          {solutionAreaOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={heroFilter || "__all__"} onValueChange={v => { setHeroFilter(v === "__all__" ? "" : v); setResourcePage(1); }}>
                        <SelectTrigger className="h-7 text-xs w-[180px] border-black/10" data-testid="filter-hero">
                          <SelectValue placeholder="Hero Product" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="__all__">All Hero Products</SelectItem>
                          {heroProductOptions.map(h => <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(formatFilter || roleTagFilter || areaFilter || heroFilter) && (
                        <button
                          onClick={() => { setFormatFilter(""); setRoleTagFilter(""); setAreaFilter(""); setHeroFilter(""); setResourcePage(1); }}
                          className="text-[11px] text-[#194449] hover:underline ml-auto"
                          data-testid="clear-filters-btn"
                        >Clear filters</button>
                      )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8">
                              <input
                                type="checkbox"
                                className="rounded border-black/20 cursor-pointer"
                                checked={resources.length > 0 && resources.every(r => selectedResourceIds.has(r.resource_id))}
                                onChange={e => {
                                  const next = new Set(selectedResourceIds);
                                  if (e.target.checked) resources.forEach(r => next.add(r.resource_id));
                                  else resources.forEach(r => next.delete(r.resource_id));
                                  setSelectedResourceIds(next);
                                }}
                                data-testid="resource-select-all"
                              />
                            </TableHead>
                            <TableHead className="text-xs">ID</TableHead>
                            <TableHead className="text-xs">Title</TableHead>
                            <TableHead className="text-xs">Format</TableHead>
                            <TableHead className="text-xs">Source</TableHead>
                            <TableHead className="text-xs">Hero Products</TableHead>
                            <TableHead className="text-xs">Role Tags</TableHead>
                            <TableHead className="text-xs">Area</TableHead>
                            <TableHead className="text-xs">Level</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">AI Tags</TableHead>
                            <TableHead className="text-xs sticky right-0 bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resources.map((r, i) => (
                            <TableRow
                              key={r.resource_id || i}
                              className={selectedResourceIds.has(r.resource_id) ? "bg-red-50/50" : ""}
                            >
                              <TableCell>
                                <input
                                  type="checkbox"
                                  className="rounded border-black/20 cursor-pointer"
                                  checked={selectedResourceIds.has(r.resource_id)}
                                  onChange={e => {
                                    const next = new Set(selectedResourceIds);
                                    if (e.target.checked) next.add(r.resource_id);
                                    else next.delete(r.resource_id);
                                    setSelectedResourceIds(next);
                                  }}
                                  data-testid={`resource-checkbox-${r.resource_id}`}
                                />
                              </TableCell>
                              <TableCell className="text-xs font-mono">{r.resource_id}</TableCell>
                              <TableCell className="text-xs max-w-[200px]">
                                {r.url ? (
                                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                                    className="hover:text-[#194449] hover:underline truncate block" title={r.title}>
                                    {r.title}
                                  </a>
                                ) : (
                                  <span className="truncate block" title={r.title}>{r.title}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-xs">{r.resource_format || r.resource_type || r.content_format || "—"}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <Badge variant="outline" className="text-[10px] border-black/10">{r.source_display || r.provider_name || r.source_connector || "—"}</Badge>
                                  {r.change_type && r.change_type !== "unchanged" && (
                                    <span className={`text-[9px] px-1.5 rounded-sm font-medium ${r.change_type === "new" ? "bg-[#D4EC8E] text-[#091F2C]" : r.change_type === "updated" ? "bg-blue-100 text-blue-800" : r.change_type === "manual_upload" ? "bg-purple-100 text-purple-700" : "bg-orange-100 text-orange-700"}`}>
                                      {r.change_type}
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1 max-w-[160px]">
                                  {(r.hero_products || []).slice(0, 2).map(p => (
                                    <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/10 text-[#194449]">{p.replace(/_/g, " ")}</span>
                                  ))}
                                  {(r.hero_products || []).length > 2 && <span className="text-[10px] text-[#64748B]">+{r.hero_products.length - 2}</span>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1 max-w-[160px]">
                                  {(r.role_tags || []).length > 0 ? (
                                    r.role_tags.map(rt => (
                                      <span key={rt} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                        rt === "Sales/Pre-Sales" ? "bg-[#FEF3C7] text-[#92400E]" :
                                        rt === "Technical" ? "bg-[#DBEAFE] text-[#1E3A8A]" :
                                        rt === "GTM" ? "bg-[#DCFCE7] text-[#14532D]" :
                                        // Legacy values "Sales" or "Pre-Sales" fall through here until migration completes
                                        (rt === "Sales" || rt === "Pre-Sales") ? "bg-[#FEF3C7] text-[#92400E]" :
                                        "bg-[#F4F3F5] text-[#64748B]"
                                      }`}>{rt === "Sales" || rt === "Pre-Sales" ? "Sales/Pre-Sales" : rt}</span>
                                    ))
                                  ) : r.course_stage ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F3F5] text-[#64748B] italic" title="No role tag yet — legacy 'stage' value">
                                      {r.course_stage.replace(/_/g, " ")}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-[#94A3B8] italic">—</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs">{(solutionAreaOptions.find(a => a.id === r.solution_area)?.display_name) || (r.solution_area || "").replace(/_/g, " ")}</TableCell>
                              <TableCell className="text-xs">{r.skill_level}</TableCell>
                              <TableCell>
                                <Badge className={`text-xs border-0 ${
                                  r.status === "Active" || r.status === "Published" ? "bg-[#D4EC8E] text-[#091F2C]" :
                                  r.status === "Staging" ? "bg-yellow-100 text-yellow-800" :
                                  r.status === "Archived" ? "bg-slate-100 text-slate-600" :
                                  r.status === "archive_candidate" ? "bg-red-100 text-red-700" :
                                  "bg-red-100 text-red-700"}`}>
                                  {r.status}
                                </Badge>
                              </TableCell>
                              {/* AI Tags */}
                              <TableCell className="max-w-[200px]">
                                {r.ai_tagged && r.ai_tags?.flat_tags ? (
                                  <div className="flex flex-wrap gap-1">
                                    {r.ai_tags.flat_tags.split(", ").slice(0, 3).map((tag, ti) => (
                                      <span key={ti} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#194449]/10 text-[#194449] whitespace-nowrap">{tag}</span>
                                    ))}
                                    {r.ai_tags.flat_tags.split(", ").length > 3 && (
                                      <span className="text-[9px] text-[#64748B]">+{r.ai_tags.flat_tags.split(", ").length - 3}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-[#94A3B8] italic">Not tagged</span>
                                )}
                              </TableCell>
                              {/* Actions - Bug 3 (June 2026): Sticky column so RCMs don't need to scroll */}
                              <TableCell className="sticky right-0 bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)]">
                                <div className="flex items-center gap-1">
                                  {/* Restore button — only for Archived */}
                                  {isArchived && (
                                    <button
                                      onClick={() => handleRestoreResource(r.resource_id)}
                                      disabled={restoringId === r.resource_id}
                                      title="Restore to Active"
                                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-[#07641D]/30 text-[#07641D] hover:bg-[#07641D]/5 disabled:opacity-40 transition-all"
                                      data-testid={`restore-btn-${r.resource_id}`}
                                    >
                                      {restoringId === r.resource_id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-3 h-3" />
                                      )}
                                      Restore
                                    </button>
                                  )}
                                  {/* Inline edit (hero products + solution area + AI tags) */}
                                  {!isArchived && (
                                    <button
                                      onClick={() => openAiTagsEditor(r)}
                                      title="Edit hero products, solution area, and AI tags inline"
                                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-yellow-500/40 text-yellow-700 hover:bg-yellow-50 transition-all"
                                      data-testid={`edit-ai-tags-btn-${r.resource_id}`}
                                    >
                                      <Edit3 className="w-3 h-3" />
                                      Edit
                                    </button>
                                  )}
                                  {/* Re-tag button */}
                                  <button
                                    onClick={() => handleRetag(r.resource_id)}
                                    disabled={retagging[r.resource_id]}
                                    title="Re-tag with AI"
                                    className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-[#194449]/20 text-[#194449] hover:bg-[#194449]/5 disabled:opacity-40 transition-all"
                                    data-testid={`retag-btn-${r.resource_id}`}
                                  >
                                    {retagging[r.resource_id] ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Sparkles className="w-3 h-3" />
                                    )}
                                    {retagging[r.resource_id] ? "Tagging…" : "Re-tag"}
                                  </button>
                                  {/* RCM Delete Request OR pending/rejected indicator */}
                                  {!isArchived && isRcm && (r.delete_request_status === "pending" ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200" data-testid={`pending-delete-${r.resource_id}`}>
                                      Delete requested
                                    </span>
                                  ) : r.delete_request_status === "rejected" ? (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 cursor-help"
                                      title={`Rejected by ${r.delete_rejection_by || "admin"}: ${r.delete_rejection_note || "No reason provided"}`}
                                      data-testid={`rejected-delete-${r.resource_id}`}
                                    >
                                      ❌ Request Rejected
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => openDeleteRequest(r)}
                                      title="Request deletion (super-admin approval required)"
                                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-all"
                                      data-testid={`request-delete-btn-${r.resource_id}`}
                                    >
                                      Request Delete
                                    </button>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!resourcesLoading && resources.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={12} className="text-center text-sm text-[#64748B] py-10">
                                {resourceSearch
                                  ? `No resources match "${resourceSearch}" in ${resourceStatusFilter || "all"} statuses.`
                                  : `No resources found for status: ${resourceStatusFilter || "all"}.`}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination footer */}
                    {resourceTotalPages > 1 && (
                      <div className="px-5 py-3 border-t border-black/5 flex items-center justify-between gap-4">
                        <span className="text-xs text-[#64748B]">
                          Page {resourcePage} of {resourceTotalPages} · {resourceTotal.toLocaleString()} total
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setResourcePage(1)}
                            disabled={resourcePage <= 1}
                            className="px-2 py-1 text-xs rounded border border-black/10 disabled:opacity-30 hover:bg-[#F4F3F5]"
                            data-testid="resource-page-first"
                          >«</button>
                          <button
                            onClick={() => setResourcePage(p => Math.max(1, p - 1))}
                            disabled={resourcePage <= 1}
                            className="px-3 py-1 text-xs rounded border border-black/10 disabled:opacity-30 hover:bg-[#F4F3F5]"
                            data-testid="resource-page-prev"
                          >Prev</button>
                          {Array.from({ length: Math.min(5, resourceTotalPages) }, (_, i) => {
                            const start = Math.max(1, Math.min(resourcePage - 2, resourceTotalPages - 4));
                            const pg = start + i;
                            if (pg > resourceTotalPages) return null;
                            return (
                              <button key={pg}
                                onClick={() => setResourcePage(pg)}
                                className={`px-3 py-1 text-xs rounded border ${pg === resourcePage ? "bg-[#194449] text-white border-[#194449]" : "border-black/10 hover:bg-[#F4F3F5]"}`}
                                data-testid={`resource-page-${pg}`}
                              >{pg}</button>
                            );
                          })}
                          <button
                            onClick={() => setResourcePage(p => Math.min(resourceTotalPages, p + 1))}
                            disabled={resourcePage >= resourceTotalPages}
                            className="px-3 py-1 text-xs rounded border border-black/10 disabled:opacity-30 hover:bg-[#F4F3F5]"
                            data-testid="resource-page-next"
                          >Next</button>
                          <button
                            onClick={() => setResourcePage(resourceTotalPages)}
                            disabled={resourcePage >= resourceTotalPages}
                            className="px-2 py-1 text-xs rounded border border-black/10 disabled:opacity-30 hover:bg-[#F4F3F5]"
                            data-testid="resource-page-last"
                          >»</button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Inline Edit Resource Dialog (AI tags + Hero Products + Solution Area + Role Tags) */}
            <Dialog open={!!aiTagsEditTarget} onOpenChange={(open) => { if (!open) { setAiTagsEditTarget(null); setAiTagsDraft(""); setHeroDraft([]); setSolutionAreaDraft(""); setRoleTagsDraft([]); } }}>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="edit-ai-tags-desc">
                <DialogHeader>
                  <DialogTitle>Edit Resource</DialogTitle>
                  <p id="edit-ai-tags-desc" className="text-xs text-[#64748B]">
                    Tweak hero products, solution area, and AI tags without un-publishing this resource. Changes propagate to recommendations immediately.
                  </p>
                </DialogHeader>
                {aiTagsEditTarget && (
                  <div className="space-y-4">
                    <div className="text-xs text-[#64748B] pb-2 border-b border-black/5">
                      <div className="font-medium text-[#091F2C]">{aiTagsEditTarget.title}</div>
                      <div className="font-mono mt-1">{aiTagsEditTarget.resource_id} · Status: <span className="font-semibold text-[#07641D]">{aiTagsEditTarget.status}</span></div>
                    </div>

                    {/* June 2026 (Bug 4b) — core descriptive fields */}
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input value={titleDraft} onChange={e => setTitleDraft(e.target.value)} className="h-9 text-xs mt-1" data-testid="inline-title-input" />
                    </div>
                    <div>
                      <Label className="text-xs">URL</Label>
                      <Input value={urlDraft} onChange={e => setUrlDraft(e.target.value)} className="h-9 text-xs mt-1" data-testid="inline-url-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Format</Label>
                        <Select value={formatDraft || ""} onValueChange={v => setFormatDraft(v)}>
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid="inline-format-select">
                            <SelectValue placeholder="Select format..." />
                          </SelectTrigger>
                          <SelectContent>
                            {FORMAT_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Level</Label>
                        <Select value={levelDraft || ""} onValueChange={v => setLevelDraft(v)}>
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid="inline-level-select">
                            <SelectValue placeholder="Select level..." />
                          </SelectTrigger>
                          <SelectContent>
                            {["Beginner", "Intermediate", "Advanced"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Solution Area</Label>
                      <Select value={solutionAreaDraft || ""} onValueChange={v => setSolutionAreaDraft(v)}>
                        <SelectTrigger className="h-9 text-xs mt-1" data-testid="inline-solution-area-select">
                          <SelectValue placeholder="Select solution area..." />
                        </SelectTrigger>
                        <SelectContent>
                          {solutionAreaOptions.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Phase 3 May 2026 — Role Tags multi-select */}
                    <div>
                      <Label className="text-xs">Role Tags (Sales/Pre-Sales · Technical · GTM)</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1" data-testid="inline-role-tags-chips">
                        {ROLE_TAG_OPTIONS.map(rt => {
                          const selected = roleTagsDraft.includes(rt);
                          return (
                            <button
                              key={rt}
                              type="button"
                              onClick={() => setRoleTagsDraft(prev =>
                                selected ? prev.filter(x => x !== rt) : [...prev, rt]
                              )}
                              className={`text-xs px-3 py-1 rounded-full border transition-all ${
                                selected
                                  ? (rt === "Sales/Pre-Sales" ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" :
                                     rt === "Technical" ? "bg-[#DBEAFE] text-[#1E3A8A] border-[#93C5FD]" :
                                     "bg-[#DCFCE7] text-[#14532D] border-[#86EFAC]")
                                  : "bg-white text-[#64748B] border-black/15 hover:bg-[#F4F3F5]"
                              }`}
                              data-testid={`role-tag-toggle-${rt}`}
                            >
                              {selected ? "✓ " : ""}{rt}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-[#64748B] mt-1.5">
                        Replaces the legacy "Stage" column. Used to filter the library and to match learners by role.
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs">Hero Products</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1 p-2 border rounded-md bg-[#F4F3F5] min-h-[40px]" data-testid="inline-hero-products-chips">
                        {(heroDraft || []).length === 0 ? (
                          <span className="text-[11px] text-[#94A3B8] italic">No hero products yet — add one below</span>
                        ) : heroDraft.map(p => (
                          <Badge key={p} className="text-xs bg-[#194449] text-white border-0 flex items-center gap-1 pr-1">
                            {heroProductOptions.find(h => h.id === p)?.display_name || p.replace(/_/g, " ")}
                            <button
                              type="button"
                              className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                              onClick={() => setHeroDraft(prev => prev.filter(hp => hp !== p))}
                              data-testid={`remove-hero-${p}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <Select
                        value=""
                        onValueChange={v => {
                          if (v && !heroDraft.includes(v)) setHeroDraft(prev => [...prev, v]);
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs mt-1.5" data-testid="inline-hero-products-add">
                          <SelectValue placeholder="Add a hero product..." />
                        </SelectTrigger>
                        <SelectContent>
                          {heroProductOptions
                            .filter(h => !heroDraft.includes(h.id))
                            .map(h => (
                              <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">AI Tags (comma-separated)</Label>
                      <Textarea
                        value={aiTagsDraft}
                        onChange={e => setAiTagsDraft(e.target.value)}
                        placeholder="e.g. Microsoft Sentinel, Data Loss Prevention, Level 200, SIEM"
                        className="text-xs min-h-[100px] mt-1"
                        data-testid="inline-ai-tags-textarea"
                      />
                      <p className="text-[10px] text-[#64748B] mt-1">
                        Updates <code>ai_tags.flat_tags</code> + <code>technology_tags</code>. Status stays unchanged.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => { setAiTagsEditTarget(null); setAiTagsDraft(""); setHeroDraft([]); setSolutionAreaDraft(""); setRoleTagsDraft([]); }}
                        variant="outline"
                        className="rounded-full flex-1 text-sm"
                        data-testid="inline-ai-tags-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveAiTags}
                        disabled={aiTagsSaving}
                        className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full flex-1 text-sm"
                        data-testid="inline-ai-tags-save-btn"
                      >
                        {aiTagsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Phase 3 May 2026 — How Plans Are Generated dialog */}
            <Dialog open={showFlowDoc} onOpenChange={setShowFlowDoc}>
              <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" aria-describedby="flow-doc-desc">
                <DialogHeader>
                  <DialogTitle>How Plans Are Generated</DialogTitle>
                  <p id="flow-doc-desc" className="text-xs text-[#64748B]">
                    9-step pipeline + 4 worked examples explaining how the recommendation engine selects + sequences resources.
                  </p>
                </DialogHeader>
                {!flowDoc ? (
                  <div className="py-12 text-center text-sm text-[#64748B]"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading flow…</div>
                ) : (
                  <div className="space-y-5 text-sm">
                    <div>
                      <h4 className="font-semibold text-[#091F2C] mb-2">Pipeline</h4>
                      <ol className="space-y-1.5">
                        {flowDoc.pipeline_steps.map(s => (
                          <li key={s.step} className="flex gap-3" data-testid={`flow-step-${s.step}`}>
                            <span className="font-mono text-xs bg-[#194449] text-white rounded-full w-5 h-5 flex-shrink-0 flex items-center justify-center">{s.step}</span>
                            <div>
                              <div className="font-medium text-[#091F2C] text-xs">{s.title}</div>
                              <div className="text-[11px] text-[#64748B] mt-0.5">{s.detail}</div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#091F2C] mb-2">Worked Examples</h4>
                      <div className="grid gap-3">
                        {flowDoc.examples.map(ex => (
                          <div key={ex.id} className="border border-black/10 rounded-lg p-3 bg-[#FFFCFA]" data-testid={`flow-example-${ex.id}`}>
                            <div className="text-xs font-semibold text-[#194449] mb-1">{ex.id}</div>
                            <pre className="text-[10px] bg-white p-2 rounded border border-black/5 overflow-x-auto">{JSON.stringify(ex.intake, null, 2)}</pre>
                            <ul className="mt-2 space-y-1">
                              {ex.why_each_resource.map((w, i) => (
                                <li key={i} className="text-[11px] text-[#64748B] flex gap-1.5">
                                  <span className="text-[#194449]">•</span>{w}
                                </li>
                              ))}
                            </ul>
                            <div className="mt-2 text-[11px] text-[#07641D] italic">Result: {ex.expected_sections}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {flowDoc.doc_link && (
                      <div className="text-[11px] text-[#64748B] border-t border-black/5 pt-3">
                        Full technical reference: <code>/app/memory/RECOMMENDATION_FLOW.md</code>
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Phase 3 May 2026 — RCM Delete-Request dialog */}
            <Dialog open={!!deleteReqTarget} onOpenChange={(open) => { if (!open) { setDeleteReqTarget(null); setDeleteReqReason(""); } }}>
              <DialogContent className="max-w-md" aria-describedby="delete-req-desc">
                <DialogHeader>
                  <DialogTitle>Request Resource Deletion</DialogTitle>
                  <p id="delete-req-desc" className="text-xs text-[#64748B]">
                    A super-admin must approve before the resource is permanently deleted.
                  </p>
                </DialogHeader>
                {deleteReqTarget && (
                  <div className="space-y-4">
                    <div className="text-xs text-[#64748B] pb-2 border-b border-black/5">
                      <div className="font-medium text-[#091F2C]">{deleteReqTarget.title}</div>
                      <div className="font-mono mt-1">{deleteReqTarget.resource_id}</div>
                    </div>
                    <div>
                      <Label className="text-xs">Reason (visible to super-admin)</Label>
                      <Textarea
                        value={deleteReqReason}
                        onChange={e => setDeleteReqReason(e.target.value)}
                        placeholder="e.g. Link is dead / Resource superseded by newer Microsoft Learn module / Vendor renamed product…"
                        className="text-xs min-h-[90px] mt-1"
                        data-testid="delete-req-reason-textarea"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => { setDeleteReqTarget(null); setDeleteReqReason(""); }}
                        variant="outline"
                        className="rounded-full flex-1 text-sm"
                        data-testid="delete-req-cancel-btn"
                      >Cancel</Button>
                      <Button
                        onClick={submitDeleteRequest}
                        disabled={deleteReqSaving}
                        className="bg-red-600 text-white hover:bg-red-700 rounded-full flex-1 text-sm"
                        data-testid="delete-req-submit-btn"
                      >
                        {deleteReqSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Submit Request
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Iter-39 May 2026 — Bulk Edit dialog (Role / Hero Products / Area / Format) */}
            <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
              <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby="bulk-edit-desc">
                <DialogHeader>
                  <DialogTitle>Bulk Edit ({selectedResourceIds.size} selected)</DialogTitle>
                  <p id="bulk-edit-desc" className="text-xs text-[#64748B]">
                    Update Role Tags, Hero Products, Solution Area, or Format on every selected resource at once.
                    Empty fields are left unchanged. Background AI re-tag is queued automatically after save.
                  </p>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <div className="flex gap-1.5 mt-1.5">
                      {[
                        { id: "replace", label: "Replace", desc: "Overwrite the existing value(s)" },
                        { id: "add",     label: "Add",     desc: "Union with existing list" },
                        { id: "remove",  label: "Remove",  desc: "Subtract from existing list" },
                      ].map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setBulkEditMode(m.id)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-all flex-1 ${
                            bulkEditMode === m.id
                              ? "bg-[#194449] text-white border-[#194449]"
                              : "bg-white text-[#64748B] border-black/15 hover:bg-[#F4F3F5]"
                          }`}
                          title={m.desc}
                          data-testid={`bulk-mode-${m.id}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#64748B] mt-1.5">
                      {bulkEditMode === "replace" && "Selected values will REPLACE each resource's current Role / Hero Products. Empty fields are left unchanged."}
                      {bulkEditMode === "add" && "Selected values will be ADDED (union) to each resource's existing list. Scalar fields (Area, Format) are ignored in Add mode."}
                      {bulkEditMode === "remove" && "Selected values will be REMOVED from each resource's existing list. Scalar fields (Area, Format) are ignored in Remove mode."}
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs">Role Tags</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bulk-role-tags-chips">
                      {ROLE_TAG_OPTIONS.map(rt => {
                        const selected = bulkRoleTags.includes(rt);
                        return (
                          <button
                            key={rt}
                            type="button"
                            onClick={() => setBulkRoleTags(prev => selected ? prev.filter(x => x !== rt) : [...prev, rt])}
                            className={`text-xs px-3 py-1 rounded-full border transition-all ${
                              selected
                                ? (rt === "Sales/Pre-Sales" ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" :
                                   rt === "Technical" ? "bg-[#DBEAFE] text-[#1E3A8A] border-[#93C5FD]" :
                                   "bg-[#DCFCE7] text-[#14532D] border-[#86EFAC]")
                                : "bg-white text-[#64748B] border-black/15 hover:bg-[#F4F3F5]"
                            }`}
                            data-testid={`bulk-role-toggle-${rt}`}
                          >
                            {selected ? "✓ " : ""}{rt}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Hero Products</Label>
                    <Select value="" onValueChange={(v) => {
                      if (v && !bulkHeroProducts.includes(v)) setBulkHeroProducts([...bulkHeroProducts, v]);
                    }}>
                      <SelectTrigger className="h-9 text-xs mt-1" data-testid="bulk-hero-select">
                        <SelectValue placeholder="Pick a hero product to add to selection..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[260px]">
                        {heroProductOptions.map(h => (
                          <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {bulkHeroProducts.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bulk-hero-chips">
                        {bulkHeroProducts.map(hp => {
                          const opt = heroProductOptions.find(o => o.id === hp);
                          return (
                            <span key={hp} className="text-[10px] bg-[#DBEAFE] text-[#1E3A8A] px-2 py-0.5 rounded inline-flex items-center gap-1">
                              {opt?.display_name || hp}
                              <button type="button" onClick={() => setBulkHeroProducts(bulkHeroProducts.filter(x => x !== hp))} className="hover:text-red-700">×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {bulkEditMode === "replace" && (
                    <>
                      <div>
                        <Label className="text-xs">Solution Area <span className="text-[#94A3B8]">(Replace mode only)</span></Label>
                        <Select value={bulkSolutionArea || "__none__"} onValueChange={v => setBulkSolutionArea(v === "__none__" ? "" : v)}>
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid="bulk-area-select">
                            <SelectValue placeholder="Leave unchanged" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__"><span className="italic text-[#94A3B8]">Leave unchanged</span></SelectItem>
                            {solutionAreaOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Content Format <span className="text-[#94A3B8]">(Replace mode only)</span></Label>
                        <Select value={bulkContentFormat || "__none__"} onValueChange={v => setBulkContentFormat(v === "__none__" ? "" : v)}>
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid="bulk-format-select">
                            <SelectValue placeholder="Leave unchanged" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[260px]">
                            <SelectItem value="__none__"><span className="italic text-[#94A3B8]">Leave unchanged</span></SelectItem>
                            {FORMAT_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Iter-39b — Preview diff panel (shown after clicking Preview) */}
                  {bulkPreview && (
                    <div className="border border-[#194449]/20 bg-[#FFFCFA] rounded-lg p-3 max-h-[280px] overflow-y-auto" data-testid="bulk-preview-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Preview ({bulkPreview.summary.mode} mode)
                        </div>
                        <button
                          onClick={() => setBulkPreview(null)}
                          className="text-[10px] text-[#64748B] hover:text-[#194449] underline"
                          data-testid="bulk-preview-back-btn"
                        >← Back to edit</button>
                      </div>
                      <div className="text-[11px] text-[#64748B] mb-2 flex flex-wrap gap-x-3 gap-y-1">
                        <span><span className="font-semibold text-[#07641D]">{bulkPreview.summary.will_change}</span> will change</span>
                        {bulkPreview.summary.no_change > 0 && <span><span className="font-semibold text-[#64748B]">{bulkPreview.summary.no_change}</span> no change</span>}
                        {bulkPreview.summary.skipped_out_of_scope > 0 && <span><span className="font-semibold text-red-600">{bulkPreview.summary.skipped_out_of_scope}</span> out of scope</span>}
                      </div>
                      {bulkPreview.diffs.length === 0 ? (
                        <div className="text-[11px] text-[#94A3B8] italic py-2">
                          No changes — every selected resource already matches the requested state.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {bulkPreview.diffs.slice(0, 30).map((d) => (
                            <div key={d.resource_id} className="border-b border-black/5 pb-2 last:border-0 last:pb-0" data-testid={`bulk-preview-row-${d.resource_id}`}>
                              <div className="text-[11px] font-medium text-[#091F2C] truncate" title={d.title}>{d.title}</div>
                              <div className="text-[10px] font-mono text-[#94A3B8]">{d.resource_id}</div>
                              {d.changed_fields.map(f => (
                                <div key={f} className="text-[10px] text-[#64748B] mt-1 ml-2 flex flex-wrap gap-x-1 items-baseline">
                                  <span className="font-semibold text-[#194449] mr-1">{f}:</span>
                                  <span className="text-red-700 line-through">{_formatArr(d.before[f])}</span>
                                  <span className="mx-1">→</span>
                                  <span className="text-[#07641D] font-medium">{_formatArr(d.after[f])}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                          {bulkPreview.diffs.length > 30 && (
                            <div className="text-[10px] text-[#64748B] italic pt-1">
                              … and {bulkPreview.diffs.length - 30} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2 border-t border-black/5">
                    <Button onClick={() => { setBulkEditOpen(false); setBulkPreview(null); }} variant="outline" className="rounded-full flex-1 text-sm" data-testid="bulk-edit-cancel">Cancel</Button>
                    {!bulkPreview ? (
                      <Button
                        onClick={previewBulkEdit}
                        disabled={bulkPreviewLoading}
                        className="bg-[#194449] text-white hover:bg-[#194449]/85 rounded-full flex-1 text-sm"
                        data-testid="bulk-edit-preview-btn"
                      >
                        {bulkPreviewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-1.5" />}
                        Preview Changes
                      </Button>
                    ) : (
                      <Button
                        onClick={confirmBulkEdit}
                        disabled={bulkEditSaving || bulkPreview.diffs.length === 0}
                        className="bg-[#07641D] text-white hover:bg-[#07641D]/90 rounded-full flex-1 text-sm disabled:opacity-50"
                        data-testid="bulk-edit-save"
                      >
                        {bulkEditSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {bulkPreview.diffs.length === 0 ? "Nothing to apply" : `Apply to ${bulkPreview.summary.will_change} resource(s)`}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <div className="bg-white rounded-xl border border-black/5 shadow-sm">
              <div className="p-5 border-b border-black/5 flex items-center justify-between">
                <h3 className="font-semibold text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Session Logs ({sessions.length})
                </h3>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={handleDownloadAnalytics} data-testid="download-analytics-btn">
                    <Download className="w-4 h-4 mr-2" /> Download CSV
                  </Button>
                  <Button variant="ghost" size="sm" onClick={loadDashboard}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Session ID</TableHead>
                      <TableHead className="text-xs">Role</TableHead>
                      <TableHead className="text-xs">Area</TableHead>
                      <TableHead className="text-xs">Level</TableHead>
                      <TableHead className="text-xs">Result</TableHead>
                      <TableHead className="text-xs">Duration</TableHead>
                      <TableHead className="text-xs">Time</TableHead>
                      <TableHead className="text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-mono max-w-[120px] truncate">{s.session_id}</TableCell>
                        <TableCell className="text-xs">{s.intake?.role || "-"}</TableCell>
                        <TableCell className="text-xs">{(s.intake?.solution_area || "-").replace("_", " ")}</TableCell>
                        <TableCell className="text-xs">{s.intake?.experience_level || "-"}</TableCell>
                        <TableCell>
                          <Badge variant={s.validation_result === "pass" ? "default" : "destructive"} className={`text-xs ${s.validation_result === "pass" ? "bg-[#D4EC8E] text-[#091F2C] border-0" : ""}`}>
                            {s.validation_result || "pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{s.duration_ms ? `${(s.duration_ms/1000).toFixed(1)}s` : "-"}</TableCell>
                        <TableCell className="text-xs">{s.created_at ? new Date(s.created_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => handleViewSession(s.session_id)} data-testid={`view-session-${i}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Session: {s.session_id?.slice(0, 8)}...</DialogTitle>
                              </DialogHeader>
                              {selectedSession && (
                                <div className="space-y-4 text-sm">
                                  <div>
                                    <h4 className="font-semibold mb-1">Intake</h4>
                                    <pre className="bg-[#F4F3F5] p-3 rounded-lg text-xs overflow-x-auto">
                                      {JSON.stringify(selectedSession.intake, null, 2)}
                                    </pre>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold mb-1">Filtered Resources ({selectedSession.filtered_resource_count || 0})</h4>
                                    <pre className="bg-[#F4F3F5] p-3 rounded-lg text-xs overflow-x-auto">
                                      {JSON.stringify(selectedSession.filtered_resource_ids, null, 2)}
                                    </pre>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold mb-1">Validation: {selectedSession.validation_result}</h4>
                                    {selectedSession.validation_error && (
                                      <p className="text-red-600 text-xs">{selectedSession.validation_error}</p>
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold mb-1">Generated Plan</h4>
                                    <pre className="bg-[#F4F3F5] p-3 rounded-lg text-xs overflow-x-auto">
                                      {JSON.stringify(selectedSession.plan, null, 2)}
                                    </pre>
                                  </div>
                                  {selectedSession.refinements && (
                                    <div>
                                      <h4 className="font-semibold mb-1">Refinements</h4>
                                      <pre className="bg-[#F4F3F5] p-3 rounded-lg text-xs overflow-x-auto">
                                        {JSON.stringify(selectedSession.refinements, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sessions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-sm text-[#64748B] py-8">
                          No sessions yet. Plans will appear here once learners start using the tool.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit">
            <div className="bg-white rounded-xl border border-black/5 shadow-sm">
              <div className="p-5 border-b border-black/5">
                <h3 className="font-semibold text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Audit Trail ({auditLogs.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Action</TableHead>
                      <TableHead className="text-xs">Admin</TableHead>
                      <TableHead className="text-xs">Details</TableHead>
                      <TableHead className="text-xs">Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{log.action}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{log.admin_email}</TableCell>
                        <TableCell className="text-xs max-w-[300px] truncate">
                          {JSON.stringify(log.details)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {auditLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-sm text-[#64748B] py-8">
                          No audit logs yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            <div className="space-y-6">
              {/* System Prompt */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6">
                <h3 className="font-semibold text-[#091F2C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  System Prompt
                </h3>
                <p className="text-sm text-[#64748B] mb-4">
                  This prompt is sent to Claude with every plan generation request. It enforces governance rules.
                </p>
                <Textarea
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  className="min-h-[200px] font-mono text-sm mb-4"
                  data-testid="system-prompt-textarea"
                />
                <Button
                  onClick={handleUpdatePrompt}
                  disabled={promptLoading}
                  className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full"
                  data-testid="update-prompt-btn"
                >
                  {promptLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Update System Prompt
                </Button>
              </div>

              {/* Dropdown Configs */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6">
                <h3 className="font-semibold text-[#091F2C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Dropdown Configurations
                </h3>
                <p className="text-sm text-[#64748B] mb-4">
                  Upload JSON files to update dropdown options for the intake form.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: "role_definitions", label: "Role Definitions" },
                    { name: "solution_areas", label: "Solution Areas" },
                    { name: "business_objectives", label: "Business Objectives" },
                  ].map(config => (
                    <div key={config.name} className="border border-black/5 rounded-lg p-4">
                      <div className="font-medium text-sm text-[#091F2C] mb-2">{config.label}</div>
                      <label className="cursor-pointer" data-testid={`upload-${config.name}-btn`}>
                        <div className="inline-flex items-center gap-2 text-[#194449] hover:text-[#07641D] text-sm font-medium transition-colors">
                          <Upload className="w-4 h-4" /> Upload JSON
                        </div>
                        <input type="file" accept=".json" className="hidden" onChange={e => handleDropdownUpload(config.name, e)} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <AdminUsers />
          </TabsContent>

          {/* LevelUp Users Tab */}
          <TabsContent value="app-users">
            <AppUserManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
